// Slice B2.1 of docs/planning/activity_dock_workflow_facilitation.md (the
// reframed "Pixie" workflow facilitator). Encapsulates the transient-peek
// lifecycle that auto-opens the dock for a short window on mode entry when
// there's a non-neutral signal to surface, then auto-collapses unless the
// user has interacted with the panel.
//
// Discipline (mirrors §4.3 of the planning doc):
//   1. One peek per (mode, session). Bouncing between modes does not
//      re-peek a mode the user has already seen this session.
//   2. Initial mount never peeks. The very first observed mode is just
//      "where the user is"; peeks are for *transitions*.
//   3. Peek only when `hasPeekWorthySignal` is true (B2.4 — derived
//      from the section registry's `hasSignal` predicates filtered by
//      per-section `modeRelevance`). Pre-B2.4 this was a single
//      amber/red rollup tone; now sections own the decision so a
//      `leads`-mode signal can't peek inside `manufacturing` mode.
//   4. Any user interaction inside the panel (mousedown, pointermove,
//      keydown, scroll) cancels the auto-close. The dock then behaves
//      as a normal user-opened panel.
//   5. If the user closes the panel before the timer fires, the timer is
//      cancelled. The mode is still marked peeked-this-session.
//
// The pure decision logic lives in `shouldPeekOnModeChange` so the React
// glue stays tiny and unit-testable without a DOM.

import { useEffect, useRef, type RefObject } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  canPeekOnModeChange,
  ACTIVITY_DOCK_ASSIST_LEVEL_DEFAULT,
  type ActivityDockAssistLevel,
} from "@/lib/activityDock/assistLevel";
import {
  addPeekedMode,
  getPeekedModes,
} from "@/lib/activityDock/peekedModesStore";
import type { DockPeekDecision } from "./dockPeekDecision";
import {
  buildActivityDockPeekEventDetail,
  emitActivityDockPeekEvent,
} from "./activityDockPeekEvent";

export const ACTIVITY_DOCK_PEEK_DURATION_MS = 5000;

export type ShouldPeekOnModeChangeInput = {
  /** Previous mode the dock has been tracking. `null` on the very first
   * observed render (initial mount); never peek in that case. */
  previousMode: PrimaryMode | null;
  /** Current mode reported by the workspace. */
  currentMode: PrimaryMode | null | undefined;
  /**
   * Slice B2.4 — `true` iff at least one role-visible, mode-relevant
   * section reports an attention-worthy signal for `currentMode`.
   * The dock computes this via `useActivityDockPeekSignal` /
   * `dockHasPeekWorthySignal`. Replaces the pre-B2.4 single-tone
   * input: now per-section `hasSignal` predicates own the decision
   * so the same "amber" state can drive a peek in `leads` and stay
   * silent in `manufacturing`.
   */
  hasPeekWorthySignal: boolean;
  /** Modes the user has already been peeked at this session. */
  alreadyPeekedModes: ReadonlySet<PrimaryMode>;
  /**
   * Slice B2.2 — user's chosen dock assist level. When omitted defaults
   * to `standard` (which lets the peek fire) so callers that haven't
   * threaded the setting yet retain pre-B2.2 behavior.
   */
  assistLevel?: ActivityDockAssistLevel;
};

/**
 * Pure predicate: returns `true` iff the dock should auto-open for a
 * transient peek given the supplied mode-change + signal context.
 */
export function shouldPeekOnModeChange(
  input: ShouldPeekOnModeChangeInput,
): boolean {
  const {
    previousMode,
    currentMode,
    hasPeekWorthySignal,
    alreadyPeekedModes,
    assistLevel = "standard",
  } = input;
  // Slice B2.2 — user has opted out of mode-change peeks (off / minimal).
  if (!canPeekOnModeChange(assistLevel)) return false;
  // No current mode yet (still bootstrapping) — never peek.
  if (currentMode == null) return false;
  // Initial mount — capture the mode but don't fire.
  if (previousMode === null) return false;
  // No transition.
  if (previousMode === currentMode) return false;
  // No section reports a peek-worthy signal for this mode (B2.4).
  // Tab badge / steady-state pill remains visible; we just don't
  // hijack attention.
  if (!hasPeekWorthySignal) return false;
  // Already peeked this mode this session.
  if (alreadyPeekedModes.has(currentMode)) return false;
  return true;
}

export type UseActivityDockModePeekArgs = {
  /** Current workspace primary mode. Optional so callers that haven't
   * threaded mode yet are no-ops. */
  currentMode: PrimaryMode | null | undefined;
  /**
   * Slice B2.4 — section-aware peek decision: `{ shouldPeek,
   * reasonSectionId }` from `useActivityDockPeekDecision`. The
   * boolean drives the auto-open lifecycle; the `reasonSectionId`
   * (B2.4-B) is forwarded to the structured `ACTIVITY_DOCK_PEEK_EVENT`
   * payload so subscribers can attribute the peek to the section
   * that earned it.
   */
  peekDecision: DockPeekDecision;
  /** Current open/closed state of the dock panel. */
  open: boolean;
  /** Setter for the open state. */
  setOpen: (next: boolean) => void;
  /** Ref to the expanded panel DOM node; used so user-interaction
   * listeners attach at the panel boundary, not document-wide. */
  panelRef: RefObject<HTMLElement | null>;
  /**
   * Slice B2.2 — user's chosen assist level. Optional; absent treated
   * as `standard` so pre-B2.2 callers retain existing behavior.
   */
  assistLevel?: ActivityDockAssistLevel;
};

/**
 * React hook: orchestrates the transient peek on mode entry. Tracks the
 * previous mode in a ref so the first render captures-without-firing.
 * Mounts a one-shot auto-close timer plus interaction listeners while a
 * peek is active; tears them down on cancel / close / unmount.
 */
export function useActivityDockModePeek(args: UseActivityDockModePeekArgs): void {
  const {
    currentMode,
    peekDecision,
    open,
    setOpen,
    panelRef,
    assistLevel,
  } = args;

  const previousModeRef = useRef<PrimaryMode | null>(null);
  const timerRef = useRef<number | null>(null);
  // Tracks whether the *current* open state was triggered by the
  // peek (so we know whether the auto-close timer should still own
  // the panel, or whether the user has taken over via interaction).
  const activePeekRef = useRef(false);

  const { shouldPeek: hasPeekWorthySignal, reasonSectionId } = peekDecision;

  useEffect(() => {
    const previousMode = previousModeRef.current;

    const willPeek = shouldPeekOnModeChange({
      previousMode,
      currentMode,
      hasPeekWorthySignal,
      // Reads the module-level set (see peekedModesStore); see the
      // dock's "Peek lifecycle" diagnostic in the dev panel for a
      // live view of which modes have already been peeked this
      // session and a "Clear" button that resets it without a reload.
      alreadyPeekedModes: getPeekedModes(),
      assistLevel,
    });

    // Capture the new mode regardless (so the next effect-run sees it
    // as `previousMode`). Done before the side-effect so a thrown
    // setOpen doesn't corrupt the previous-ref.
    if (currentMode != null && currentMode !== previousMode) {
      previousModeRef.current = currentMode;
    }

    if (!willPeek) return;
    if (currentMode == null) return;

    addPeekedMode(currentMode);
    activePeekRef.current = true;
    setOpen(true);

    // Slice B2.4-B — telemetry: emit a structured peek-fire event so
    // future deadweight analysis (dev-only stats panel, server beacon,
    // etc.) can attribute the peek to the section that earned it. We
    // fire AFTER `setOpen(true)` so a subscriber only sees fires that
    // actually opened the panel — telemetry tracks reality, not
    // intent. `reasonSectionId` is non-null whenever `shouldPeek` is
    // true (the decision helper sets them together), but we guard
    // anyway so a future shape drift can't accidentally emit
    // `reasonSectionId: null` into the payload.
    if (reasonSectionId != null) {
      emitActivityDockPeekEvent(
        buildActivityDockPeekEventDetail({
          mode: currentMode,
          reasonSectionId,
          assistLevel: assistLevel ?? ACTIVITY_DOCK_ASSIST_LEVEL_DEFAULT,
          nowMs: Date.now(),
        }),
      );
    }

    if (typeof window === "undefined") return;
    timerRef.current = window.setTimeout(() => {
      // Only auto-close if the user hasn't taken over via interaction.
      if (activePeekRef.current) {
        activePeekRef.current = false;
        setOpen(false);
      }
      timerRef.current = null;
    }, ACTIVITY_DOCK_PEEK_DURATION_MS);

    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      activePeekRef.current = false;
    };
  }, [currentMode, hasPeekWorthySignal, reasonSectionId, setOpen, assistLevel]);

  // If the panel closes (by any means — user × button, click-outside,
  // ESC) cancel any in-flight auto-close timer so a stale callback
  // can't re-toggle visibility.
  useEffect(() => {
    if (open) return;
    if (timerRef.current == null) return;
    if (typeof window === "undefined") return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
    activePeekRef.current = false;
  }, [open]);

  // While a peek is active, cancel the auto-close on any in-panel
  // interaction. The user has taken over; the panel sticks until they
  // close it themselves.
  useEffect(() => {
    if (!open) return;
    if (!activePeekRef.current) return;
    const node = panelRef.current;
    if (!node) return;
    if (typeof window === "undefined") return;

    function cancel(): void {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      activePeekRef.current = false;
    }

    node.addEventListener("mousedown", cancel);
    node.addEventListener("pointermove", cancel);
    node.addEventListener("keydown", cancel);
    node.addEventListener("scroll", cancel, true);
    return () => {
      node.removeEventListener("mousedown", cancel);
      node.removeEventListener("pointermove", cancel);
      node.removeEventListener("keydown", cancel);
      node.removeEventListener("scroll", cancel, true);
    };
  }, [open, panelRef]);
}
